import externalTriggerAdapter from 'apps/externalTriggers/externalTriggerAdapter'
import widgetAdapter from 'apps/growthTools/models/Widget/adapter'
import triggerRuleAdapter from 'apps/triggerRules/models/TriggerRule/adapter'
import type { IAPI } from 'common/core/interfaces/api'

export const fetchBotSettings = '/:currentAccountID/settings/data'

export const fetchIntegromatWebhooks = '/:currentAccountID/integration/integromatHooks'

export const uploadAttachment = '/:currentAccountID/content/upload'

export const fetchSharedFlow = '/manychat/getSharedFlow?share_hash=:share_hash'

export const audience: IAPI = {
  idKey: 'user_id',
  itemKey: 'user',
  listKey: 'users',
  endpoints: {
    fetch: '/:currentAccountID/subscribers/search',
    // Single subscriber actions
    importSubscribers: '/:currentAccountID/subscribers/importUnifiedSubscribers',
    createSubscriber: '/:currentAccountID/subscribers/createUnifiedSubscriber',
  },
}

export const threads: IAPI = {
  idKey: 'user_id',
  itemKey: 'thread',
  listKey: 'threads',
  endpoints: {
    fetchThreadCounters: '/:currentAccountID/im/getLiveChatCounters',
  },
}

export const liveChatSearch: IAPI = {
  idKey: 'message_id',
  listKey: 'messages',
  endpoints: {
    fetch: '/:currentAccountID/im/searchMessage',
  },
}

export const chatAdmins: IAPI = {
  idKey: 'user_id',
  listKey: 'admins',
  endpoints: {
    fetch: '/:currentAccountID/im/admins',
  },
}

export const tagsFolders: IAPI = {
  idKey: 'folder_id',
  itemKey: 'folder',
  listKey: 'folders',
  endpoints: {
    fetch: ['/:currentAccountID/folders/list', { method: 'POST' }],
    create: ['/:currentAccountID/folders/create', { method: 'POST' }],
    rename: '/:currentAccountID/folders/rename',
    delete: ['/:currentAccountID/folders/delete', { method: 'POST' }],
  },
}

export const userFieldFolders: IAPI = {
  idKey: 'folder_id',
  itemKey: 'folder',
  listKey: 'folders',
  endpoints: {
    fetch: ['/:currentAccountID/folders/list', { method: 'POST' }],
    create: ['/:currentAccountID/folders/create', { method: 'POST' }],
    rename: ['/:currentAccountID/folders/rename', { method: 'POST' }],
    delete: ['/:currentAccountID/folders/delete', { method: 'POST' }],
  },
}

export const globalFieldFolders: IAPI = {
  idKey: 'folder_id',
  itemKey: 'folder',
  listKey: 'folders',
  endpoints: {
    fetch: ['/:currentAccountID/folders/list', { method: 'POST' }],
    create: ['/:currentAccountID/folders/create', { method: 'POST' }],
    rename: ['/:currentAccountID/folders/rename', { method: 'POST' }],
    delete: ['/:currentAccountID/folders/delete', { method: 'POST' }],
  },
}

export const targetPageGlobalFields: IAPI = {
  idKey: 'field_id',
  itemKey: 'field',
  listKey: 'fields',
  endpoints: {
    fetch: '/:pageId/globalFields/list?active_only=:active_only',
  },
}

export const customEvents: IAPI = {
  idKey: 'event_id',
  itemKey: 'custom_event',
  listKey: 'list',
  endpoints: {
    fetch: '/:currentAccountID/customEvents/list', // ?active_only=:active_only
    create: ['/:currentAccountID/customEvents/create', { method: 'POST' }],
    update: '/:currentAccountID/customEvents/update',
    save: ['/:currentAccountID/customEvents/update', { method: 'POST' }],
    archive: ['/:currentAccountID/customEvents/archive', { method: 'POST' }],
    restore: ['/:currentAccountID/customEvents/restore', { method: 'POST' }],
    delete: '/:currentAccountID/customEvents/delete',
    setValue: ['/:currentAccountID/customEvents/changeValue', { method: 'POST' }],
    getEntitiesList: ['/:currentAccountID/customEvents/getUsesList'],
  },
}

export const widgets: IAPI = {
  idKey: 'widget_id',
  itemKey: 'widget',
  listKey: 'widgets',
  import: widgetAdapter,
  endpoints: {
    fetch: '/:currentAccountID/growth-tools/list',
    fetchItem: '/:currentAccountID/growth-tools/loadWidget?widget_id=:id',
    create: [
      '/:currentAccountID/growth-tools/createWidget?widget_type=:type&name=:name&ns=:ns',
      { method: 'POST' },
    ],
    save: ['/:currentAccountID/growth-tools/setWidget', { method: 'POST' }],
    saveStatus: [
      '/:currentAccountID/growth-tools/setDraftStatus?widget_id=:id&status=:status',
      { method: 'POST' },
    ],
    delete: '/:currentAccountID/growth-tools/deleteWidget?widget_id=:id',
    copy: '/:currentAccountID/growth-tools/copyWidget?widget_id=:id',
  },
}

export const forms: IAPI = {
  idKey: 'content_id',
  itemKey: 'form',
  listKey: 'forms',
  endpoints: {
    fetch: '/:currentAccountID/forms/getList',
    fetchItem: '/:currentAccountID/forms/getAnswers?content_id=:id&limit=1',
    markAsRead: ['/:currentAccountID/forms/markAsRead', { method: 'POST' }],
  },
}

export const answers: IAPI = {
  idKey: 'id',
  itemKey: 'answer',
  listKey: 'answers',
  endpoints: {
    fetch: '/:currentAccountID/forms/getAnswers?content_id=:id',
    prepareExport: '/:currentAccountID/forms/prepareAnswersExport',
    download: '/forms/downloadAnswersExport?export_id=:exportId&content_id=:contentId',
  },
}

export const subscribers: IAPI = {
  idKey: 'user_id',
  itemKey: 'user',
  listKey: 'users',
  endpoints: {
    fetch: '/:currentAccountID/subscribers/search',
    countSubscribers: '/:currentAccountID/subscribers/count',
  },
}

export const sponsoredMessages: IAPI = {
  endpoints: {
    loadTableData:
      '/:currentAccountID/ads/getTableData?with_growth_tools=true&with_ad_set_drafts=true&with_ad_accounts=true&with_ads=true&with_stats=true&with_currencies=true',
    enablePageAds: ['/:currentAccountID/ads/enable', { method: 'POST' }],
    loadAdAccounts: '/:currentAccountID/ads/allAdAccounts',
    getAdAccountCurrency: '/ads/fb/adAccount/getCurrency?ad_account_id=:ad_account_id',
    createAdCampaign: '/ads/fb/campaign/create',
    getAdCampaigns: '/:currentAccountID/ads/allCampaigns?ad_account_id=:ad_account_id',
    updateCampaignName: '/ads/fb/campaign/update',
    createAdSet: '/ads/fb/adset/create',
    getAdSets: '/:currentAccountID/ads/allAdSets?campaign_id=:campaign_id',
    updateAdSet: '/ads/fb/adset/update',
    fetchAdSetDraft: '/ads/fb/adset/getDraft?ad_set_draft_id=:ad_set_draft_id&with_ad_set=true',
    fetchAdSet: '/ads/fb/adset/get?ad_set_id=:ad_set_id&with_ad_set_draft=true',
    publishAdSet: '/ads/fb/adset/publish',
    getCustomAudience: '/ads/fb/adAccount/getCustomAudience?custom_audience_id=:custom_audience_id',
    createCustomAudience: '/ads/fb/adAccount/createCustomAudience',
    deleteCustomAudience: '/ads/fb/adAccount/deleteCustomAudience',
    addUsersToCustomAudience: '/ads/fb/adAccount/addUsersToCustomAudience',
    createAd: '/:currentAccountID/ads/growthTool/create',
    fetchAdDraft: '/:currentAccountID/ads/growthTool/get?id=:ad_draft_id',
    saveAdDraft: '/:currentAccountID/ads/growthTool/saveDraft',
    publishAd: '/:currentAccountID/ads/growthTool/publish',
    deleteAd: '/:currentAccountID/ads/growthTool/delete',
    duplicateAd: '/:currentAccountID/ads/growthTool/clone',
    updateAdName: '/:currentAccountID/ads/growthTool/updateTitle',
  },
}

export const cms: IAPI = {
  endpoints: {
    getObject: '/:currentAccountID/cms/getObject',
    importSharedFlow: `/:pageId/flow/importSharedFlow`,
    moveTrigger: '/:currentAccountID/flow/moveTrigger',
    overallList: '/:currentAccountID/flow/list',
    bulkAction: '/:currentAccountID/cms/bulk',
  },
}

export const flow: IAPI = {
  endpoints: {
    patchFlowDraft: '/:currentAccountID/flow/patchDraft',
    setFlowDraft: '/:currentAccountID/flow/setDraft',
    discardFlowChanges: '/:currentAccountID/flow/discardChanges',
    publishFlow: '/:currentAccountID/flow/publish',
    createFirstRecurringFlowWithMessage: '/:currentAccountID/flow/createDefaultTopicFlow',
  },
}

export const content: IAPI = {
  endpoints: {
    getContent: '/:currentAccountID/content/data',
    getContentWithStats: '/:currentAccountID/content/view',
    sendPreview: '/:currentAccountID/content/preview',
    getPreviewRefURL: '/:currentAccountID/content/previewRefUrl',
    testRequest: '/:currentAccountID/content/:entity',
    getUserVariables: '/:currentAccountID/content/getUserVariables',
  },
}

export const triggerRules: IAPI = {
  idKey: 'rule_id',
  itemKey: 'trigger_rule',
  listKey: 'trigger_rules',
  import: triggerRuleAdapter,
  endpoints: {
    fetch: '/:currentAccountID/triggerRule/list',
    fetchItem: '/:currentAccountID/triggerRule/get?rule_id=:id',
    create: ['/:currentAccountID/triggerRule/create', { method: 'POST' }],
    copy: ['/:currentAccountID/triggerRule/copy', { method: 'POST' }],
    delete: ['/:currentAccountID/triggerRule/delete', { method: 'POST' }],
    restore: ['/:currentAccountID/triggerRule/restore', { method: 'POST' }],
    changeStatus: ['/:currentAccountID/triggerRule/changeStatus', { method: 'POST' }],
    save: ['/:currentAccountID/triggerRule/update', { method: 'POST' }],
    setTitle: ['/:currentAccountID/triggerRule/setTitle', { method: 'POST' }],
  },
}

export const externalTriggers: IAPI = {
  idKey: 'external_trigger_id',
  itemKey: 'external_trigger',
  import: externalTriggerAdapter,
  endpoints: {
    fetchItem: '/:currentAccountID/externalTrigger/get?external_trigger_id=:id',
    create: ['/:currentAccountID/externalTrigger/create'],
    save: ['/:currentAccountID/externalTrigger/update', { method: 'POST' }],
    delete: ['/:currentAccountID/externalTrigger/delete', { method: 'POST' }],
    setTitle: ['/:currentAccountID/externalTrigger/setTitle', { method: 'POST' }],
    changeStatus: ['/:currentAccountID/externalTrigger/changeStatus', { method: 'POST' }],
  },
}

export const ads: IAPI = {
  endpoints: {
    enable: '/ads/enable',
    openFeature: ['/ads/openFeature', { method: 'POST' }],
    upload: '/ads/fb/file/upload',
    getVideo: '/ads/fb/file/getVideo',
    updateAdStatus: '/ads/fb/ad/updateStatus',
    enablePage: ['/:currentAccountID/ads/enable', { method: 'POST' }],
    getTableData:
      '/:currentAccountID/ads/getTableData?with_growth_tools=true&with_ad_set_drafts=true&with_ad_accounts=true&with_campaigns=true&with_ad_sets=true&with_ads=true&with_stats=true&with_currencies=true',
    getCampaignTree: '/:currentAccountID/ads/getCampaignTree?campaign_id=:campaign_id',
    getAccountsInfo: '/:currentAccountID/ads/fullInfo?needed_accounts=true&needed_campaigns=true',
    getAccountInfo:
      '/:currentAccountID/ads/fullInfo?ad_account_id=:ad_account_id&needed_campaigns=true',
    getAd: '/ads/fb/ad/get?ad_id=:ad_id',
    allAdAccounts: '/:currentAccountID/ads/allAdAccounts',
    allCampaigns: '/:currentAccountID/ads/allCampaigns?ad_account_id=:ad_account_id',
    allAdSets: '/:currentAccountID/ads/allAdSets?campaign_id=:campaign_id',
    getCurrency: '/ads/fb/adAccount/getCurrency?ad_account_id=:ad_account_id',
    createCustomAudience: '/ads/fb/adAccount/createCustomAudience',
    deleteCustomAudience: '/ads/fb/adAccount/deleteCustomAudience',
    checkAddingAbility: '/ads/fb/adAccount/checkAddingAbility',
    createLookalikeAudience: '/ads/fb/adAccount/createLookalikeAudience',
    addUsersToCustomAudience: '/ads/fb/adAccount/addUsersToCustomAudience',
    getCustomAudiences: '/ads/fb/adAccount/getListCustomAudiences?ad_account_id=:ad_account_id',
    getCustomAudienceUserActionNames:
      '/:currentAccountID/ads/getCustomAudienceUserActionNames?ad_account_id=:ad_account_id&custom_audience_id=:custom_audience_id&user_id=:user_id',
    getLookalikeSourcePages: '/ads/fb/adAccount/getLookalikeSourcePages?business_id=:business_id',
  },
}

export const adCampaigns: IAPI = {
  endpoints: {
    create: '/ads/fb/campaign/create',
    save: '/ads/fb/campaign/update',
    updateStatus: '/ads/fb/campaign/updateStatus',
    delete: '/ads/fb/campaign/delete',
    get: '/ads/fb/campaign/get?campaign_id=:campaignId',
    fetchItem: '/ads/fb/campaign/get?campaign_id=:id',
  },
}

export const adSets: IAPI = {
  endpoints: {
    getPublished: '/ads/fb/adset/get?ad_set_id=:ad_set_id&with_ad_set_draft=true',
    getDraft: '/ads/fb/adset/getDraft?ad_set_draft_id=:ad_set_draft_id&with_ad_set=true',
    create: ['/ads/fb/adset/create', { method: 'POST' }],
    save: ['/ads/fb/adset/update', { method: 'POST' }],
    publish: '/ads/fb/adset/publish',
    estimate: '/ads/fb/adset/estimate',
    updateStatus: '/ads/fb/adset/updateStatus',
    discardDraft: '/ads/fb/adset/discardDraft',
    delete: '/ads/fb/adset/delete',
    targeting: {
      searchLocations:
        '/ads/fb/adset/searchTarget?type=adgeolocation&q=:query&params[limit]=:limit',
      searchCountyies:
        '/ads/fb/adset/searchTarget?type=adgeolocation&q=:query&params[limit]=:limit&&params[location_types]=["country","country_group"]',
      searchDetailedTargeting:
        '/ads/fb/adset/getTargetingSearch?ad_account_id=:ad_account_id&q=:query',
      getLocationsMeta:
        '/ads/fb/adset/searchTarget?type=adgeolocationmeta' +
        '&params[countries]=:countries' +
        '&params[regions]=:regions' +
        '&params[cities]=:cities' +
        '&params[zips]=:zips' +
        '&params[places]=:places' +
        '&params[custom_locations]=:custom_locations' +
        '&params[geo_markets]=:geo_markets' +
        '&params[electoral_districts]=:electoral_districts' +
        '&params[country_groups]=:country_groups',
      getLocales: '/ads/fb/adset/searchTarget?type=adlocale&q=&params[limit]=1000',
    },
    getPlatforms:
      '/ads/fb/adset/getPlacements?device_type=:device_type&objective_type=:objective_type',
  },
}

export const adgt: IAPI = {
  endpoints: {
    fetch: '/:currentAccountID/ads/growthTool/get?id=:ad_id',
    create: '/:currentAccountID/ads/growthTool/create',
    updateTitle: '/:currentAccountID/ads/growthTool/updateTitle',
    publish: '/:currentAccountID/ads/growthTool/publish',
    saveDraft: '/:currentAccountID/ads/growthTool/saveDraft',
    preview: '/:currentAccountID/ads/growthTool/preview',
    delete: '/:currentAccountID/ads/growthTool/delete',
    discardChanges: '/:currentAccountID/ads/growthTool/discardChanges',
    duplicate: '/:currentAccountID/ads/growthTool/clone',
  },
}

export const invitation: IAPI = {
  endpoints: {
    fetch: '/auth/getInvitationData?code=:code',
    accept: '/auth/processInvitation',
  },
}

export const support: IAPI = {
  idKey: 'article_id',
  listKey: 'articles',
  endpoints: {
    createTicket: '/ticket/create',
    fetch: '/ticket/articles?section=:section',
    fetchWarnings: '/supportNotices/getVisible',
  },
}

export const devLogs: IAPI = {
  idKey: 'trace_id',
  listKey: 'list',
  endpoints: {
    fetch: '/:currentAccountID/devLog/list',
    markAsRead: ['/:currentAccountID/devLog/markAsRead', { method: 'POST' }],
    fetchPolicyEnforcementLogs: '/:currentAccountID/settings/listPolicyEnforcementLogs',
  },
}

export const agency: IAPI = {
  endpoints: {
    sendMessage: ['/agency/:agency_id/sendMessage', { method: 'POST' }],
    requestConnectPageToAgency: [
      '/:currentAccountID/settings/requestConnectPageToAgency',
      { method: 'POST' },
    ],
    clickConfirm: '/agency/confirmChangeEmail',
    getStatus: '/agency/:agencyId/getStatus',
    getAchievements: '/agency/:agencyId/getAchievements',
    getBenefitsLinks: '/agency/:agencyId/getBenefitLinks',
    sendVerification: '/agency/:agency_id/sendVerificationCode',
    getPageListWithCommission:
      '/agency/getPageListWithCommission?agency_id=:agency_id&date_start=:start_date&date_end=:end_date',
    getCommissionByPage:
      '/agency/getCommissionByPage?agency_id=:agency_id&page_id=:page_id&date_start=:start_date&date_end=:end_date',
    getMatched: '/agency/getMatched',
  },
}

export const agency_signon: IAPI = {
  endpoints: {
    fetch: '/agency/get',
    create: '/agency/createEmpty',
    update: '/agency/update',
    uploadLogo: '/agency/uploadLogo',
    sendEmail: '/agency/sendEmail',
  },
}

export const agency_listing: IAPI = {
  idKey: 'agency_id',
  listKey: 'agencies',
  endpoints: {
    fetch: '/agency/agenciesList',
    fetchInfo: '/agency/getAgency?agency_id=:agency_id',
    getFilters: '/agency/agenciesFilters',
  },
}

export const events: IAPI = {
  listKey: 'events',
  endpoints: {
    fetch: '/:currentAccountID/customEvents/list',
    create: '/:currentAccountID/customEvents/create',
    update: '/:currentAccountID/customEvents/update',
    archive: '/:currentAccountID/customEvents/archive',
    getUsesList: '/:currentAccountID/customEvents/getUsesList',
    getAvailableCurrenciesList: '/:currentAccountID/customEvents/getCurrencyList',
  },
}

export const followUp: IAPI = {
  endpoints: {
    checkRnOptin: '/:currentAccountID/followUp/checkRnOptin',
  },
}
